import { useMyUser } from '@consigli/hooks';
import { type CSSProperties, type FC, useMemo } from 'react';

import en from '@/locales/en/translation.json';
import { type CatalogueViewer, agentsCatalogue } from '@/util/agents-catalogue';

import { AGENT_MARKS } from './agent-marks';
import './agent-logo-marquee.css';

export interface MarqueeLogo {
  name: string;
  svg: string;
  title: string;
}

/**
 * Resolves a `services.<slug>` key against the English bundle. Falls back to
 * the key itself rather than throwing: a catalogue entry whose title is missing
 * from `en/translation.json` should sort oddly in a decorative strip, not turn
 * the loading screen into the route error page. `agent-logo-marquee.test.tsx`
 * pins that no entry currently needs the fallback.
 */
export const englishTitle = (titleKey: string): string => {
  const value = titleKey.split('.').reduce<unknown>((node, key) => {
    if (typeof node === 'object' && node !== null && key in node) {
      return (node as Record<string, unknown>)[key];
    }
    return undefined;
  }, en);
  return typeof value === 'string' ? value : titleKey;
};

/**
 * The marks the strip shows this user: the agents directory's own catalogue
 * (`agentsCatalogue`, so the strip cannot advertise something `/agents` would
 * not list), minus any agent design has not drawn yet, alphabetical by English
 * title.
 *
 * Product's call (2026-09-16): the list the directory uses, not the whole set
 * the design system ships — so no `general-chat` (no service) or `mep` (Ceiling
 * plan is off the list), and nothing still on the fallback tile.
 *
 * Derived, not listed, so a design-system bump that adds a mark or a change to
 * product's list flows through without an edit here. English rather than the
 * active locale: the strip is decorative and aria-hidden, the order only has to
 * be stable, and sorting through `t()` would re-order the animation on a
 * language switch.
 */
export const marqueeLogos = (user: CatalogueViewer): MarqueeLogo[] =>
  agentsCatalogue(user)
    .flatMap((service) => {
      const svg = AGENT_MARKS[service.serviceName];
      return svg === undefined
        ? []
        : [{ name: service.serviceName, svg, title: englishTitle(service.titleKey) }];
    })
    .sort((a, b) => a.title.localeCompare(b.title, 'en'));

/**
 * One pass of the strip.
 *
 * `data-ds-agent-logo-inline` is doing real work, not decoration: every mark
 * paints its glyph with `.ds-agent-logo__mark-fill`, which is
 * `fill: currentColor`, and `.ds-agent-logo` sets `color: inherit`. That
 * attribute is the only rule in the design system's Logos.css that supplies
 * `--ds-color-text-invert`, and none of the mark files carry it themselves.
 * Without it the glyphs inherit the page's text colour and render dark on the
 * dark-green tile instead of white.
 *
 * The marks arrive as SVG source strings rather than components, so
 * `dangerouslySetInnerHTML` is the only way in — the same approach
 * libs/shadcn/src/components/chart.tsx:70 uses for its generated CSS.
 */
const LogoRun: FC<{ logos: MarqueeLogo[] }> = ({ logos }) => (
  <div className="agent-logo-marquee__pass">
    {logos.map((logo) => (
      <span
        className="agent-logo-marquee__item"
        data-agent={logo.name}
        data-ds-agent-logo-inline
        dangerouslySetInnerHTML={{ __html: logo.svg }}
        key={logo.name}
      />
    ))}
  </div>
);

export interface AgentLogoMarqueeProps {
  /**
   * Which way the marks travel. `'left'` is the original right-to-left pan and
   * the default, so the loading state reads exactly as it did. `'right'` plays
   * the same track backwards — see the `animation-direction` note in the
   * stylesheet for why that keeps the seam maths untouched.
   */
  direction?: 'left' | 'right';
  /**
   * Custom properties for the caller to size the strip with, today
   * `--agent-logo-marquee-max-width`. Merged under the count the component
   * owns, so a caller cannot accidentally break the pan speed.
   */
  style?: CSSProperties;
}

/**
 * The agent logos panning, for the Agents tab's loading state and the agents
 * intro dialog's header band (two instances, one each way).
 *
 * Two passes, because a seamless loop needs the strip to be twice the width it
 * translates: the animation moves exactly -50%, so pass two occupies the space
 * pass one has vacated and the seam never shows. Each pass is a wrapper so the
 * clone can be dropped under `prefers-reduced-motion` — centring both passes
 * puts the visible window across the seam, which shows the first mark in the
 * middle of the row instead of at its start. Both passes are identical; since
 * design-styles 0.26.0 the marks carry no `<filter>`, so the duplicated ids
 * that once resolved to the first copy's are gone with them.
 *
 * `aria-hidden` on the track rather than on the second pass: the marks already
 * ship `aria-hidden="true"` individually, so hiding the clone would assert
 * nothing. The whole strip is decorative — the label beside the spinner is what
 * announces the wait, and in the dialog the title and bullets are the content.
 */
export const AgentLogoMarquee: FC<AgentLogoMarqueeProps> = ({ direction = 'left', style }) => {
  const { user } = useMyUser();
  const logos = useMemo(() => marqueeLogos(user), [user]);

  return (
    <div
      aria-hidden
      className="agent-logo-marquee"
      data-direction={direction}
      data-testid="agent-logo-marquee"
      // Spread first, so the count the stylesheet multiplies by a per-mark
      // duration is the component's and not the caller's: the pan runs at the
      // same speed whether the user sees fifteen marks or sixteen.
      style={{ ...style, '--agent-logo-marquee-count': logos.length } as CSSProperties}
    >
      <div className="agent-logo-marquee__track">
        <LogoRun logos={logos} />
        <LogoRun logos={logos} />
      </div>
    </div>
  );
};
